/**
 * Application Routes Configuration
 *
 * Dynamic Route System:
 * - Public routes: Defined here (login, SSO callback)
 * - Protected routes: Generated dynamically from menuList (backend API)
 * - Component registry: See componentRegistry.ts
 *
 * To add new feature:
 * 1. Add to MFUN_TBL database
 * 2. Add to componentRegistry.ts
 * 3. Done! Route auto-generates
 */
import { type ComponentType } from 'react';

// Public routes - Load immediately (small bundle size)
import Login from '@/features/auth';
import SSOCallback from '@/features/auth/components/SSOCallback';
import { NotFoundPage, ForbiddenPage } from '@/shared/components/feedback';

export interface RouteConfig {
  path: string;
  component: ComponentType;
  index?: boolean;
  meta?: {
    title?: string;
    requiresAuth?: boolean;
    permissions?: string[];
    breadcrumb?: string[];
    description?: string;
    /**
     * Function ID from MFUN_TBL for permission checking.
     * OPTIONAL: If not specified, ProtectedRoute will auto-detect from path using menuList.
     * Auto-detection uses convention: /user → USER, /role → ROLE, / → DASHBOARD
     *
     * Explicitly specify functionId if:
     * - Route path doesn't match convention
     * - Need to override auto-detection
     * - Want explicit documentation
     */
    functionId?: string;
  };
}

/** Routes that don't require authentication */
export const publicRoutes: RouteConfig[] = [
  {
    path: '/login',
    component: Login,
    meta: {
      title: 'Login',
      requiresAuth: false,
    },
  },
  {
    path: '/sso-callback',
    component: SSOCallback,
    meta: {
      title: 'SSO Callback',
      requiresAuth: false,
    },
  },
];

/**
 * Protected routes are now generated dynamically!
 * See: dynamicRoutes.ts -> generateProtectedRoutes()
 *
 * Routes are generated from:
 * 1. menuList (from backend API)
 * 2. componentRegistry (functionId -> component mapping)
 *
 * DO NOT ADD ROUTES HERE - Use componentRegistry.ts instead
 */
export const protectedRoutes: RouteConfig[] = [];
// ⚠️ This is now empty - routes generated dynamically in AppRouter
// See: getProtectedRoutes(menuList) in dynamicRoutes.ts

/** Forbidden route for access denied */
export const forbiddenRoute: RouteConfig = {
  path: '/403',
  component: ForbiddenPage,
  meta: {
    title: '403 Forbidden',
    requiresAuth: false,
  },
};

/** Catch-all route for 404 pages */
export const notFoundRoute: RouteConfig = {
  path: '*',
  component: NotFoundPage,
  meta: {
    title: '404 Not Found',
    requiresAuth: false,
  },
};
